import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  listJobs,
  createJob,
  publishJob,
  type JobDto,
} from '../../lib/recruitment';
import { downloadCSV, downloadJSON } from '../../lib/exportUtils';
import { listDivisions, type DivisionDto } from '../../lib/divisions';
import { listOrgLevels, type OrgLevelDto } from '../../lib/orgLevels';

export const Route = createFileRoute('/rekrut/jobs/')({
  component: JobsPage,
});

function JobsPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <JobsContent />
    </ProtectedRoute>
  );
}

const STATUS_STYLES: Record<JobDto['status'], string> = {
  draft: 'bg-gray-100 text-gray-600',
  published: 'bg-green-100 text-green-700',
  closed: 'bg-red-100 text-red-600',
};

function JobsContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:rekrut');

  const [jobs, setJobs] = useState<JobDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', department: '', description: '' });
  const [formDivisionId, setFormDivisionId] = useState('');
  const [formOrgLevelId, setFormOrgLevelId] = useState('');
  const [formNewDivision, setFormNewDivision] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [orgLevels, setOrgLevels] = useState<OrgLevelDto[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listJobs(companyId),
      listDivisions(companyId),
      listOrgLevels(companyId),
    ]).then(([jobsRes, divRes, orgRes]) => {
      if (cancelled) return;
      setLoading(false);
      if (jobsRes.success && jobsRes.data) setJobs(jobsRes.data.jobs);
      if (divRes.success && divRes.data) setDivisions(divRes.data.divisions);
      if (orgRes.success && orgRes.data) setOrgLevels(orgRes.data.orgLevels);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    const divisionName = formNewDivision
      ? form.department
      : (divisions.find((d) => d.id === formDivisionId)?.name ?? form.department);
    const res = await createJob({
      companyId,
      title: form.title,
      department: divisionName,
      divisionId: formNewDivision ? undefined : (formDivisionId || undefined),
      orgLevelId: formOrgLevelId || undefined,
      description: form.description,
      requiredCompetencies: [],
    });
    setCreating(false);
    if (res.success && res.data) {
      setJobs((prev) => [...prev, res.data!.job]);
      setShowCreate(false);
      setForm({ title: '', department: '', description: '' });
      setFormDivisionId('');
      setFormOrgLevelId('');
      setFormNewDivision(false);
    } else {
      setCreateError(res.error ?? 'Failed to create job');
    }
  }

  async function handlePublish(job: JobDto) {
    const res = await publishJob({ companyId, jobId: job.id });
    if (res.success && res.data) {
      setJobs((prev) => prev.map((j) => (j.id === job.id ? res.data!.job : j)));
    }
  }

  function handleExportCSV() {
    const headers = ['Title', 'Department', 'Status', 'Description', 'Required Competencies', 'Created'];
    const rows = jobs.map((j) => [
      j.title,
      j.department,
      j.status,
      (j.description ?? '').slice(0, 100),
      Array.isArray(j.requiredCompetencies) ? j.requiredCompetencies.map((c: any) => c.name ?? c.code ?? c).join('; ') : '',
      j.createdAt ? new Date(j.createdAt).toLocaleDateString() : '',
    ]);
    downloadCSV(headers, rows, `job-postings-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  function handleExportJSON() {
    downloadJSON(jobs, `job-postings-${new Date().toISOString().slice(0, 10)}.json`);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to={'/rekrut' as any} className="text-sm text-blue-600 hover:underline">← Back to Rekrut</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Job Postings</h1>
        </div>
        <div className="flex items-center gap-2">
          {jobs.length > 0 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleExportCSV}
                className="px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={handleExportJSON}
                className="px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
              >
                Export JSON
              </button>
            </div>
          )}
          {canManage && (
            <button type="button" onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
              + New Job
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-10 text-center">
          <p className="text-gray-400">No job postings yet.</p>
          {canManage && <button type="button" onClick={() => setShowCreate(true)} className="mt-3 text-sm text-blue-600 underline">Create the first job</button>}
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Title</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase hidden sm:table-cell">Department</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{job.title}</td>
                  <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">
                    {job.divisionId
                      ? (divisions.find((d) => d.id === job.divisionId)?.name ?? job.department)
                      : job.department}
                    {job.orgLevelId && (
                      <span className="ml-1.5 text-xs text-gray-400">
                        · {orgLevels.find((o) => o.id === job.orgLevelId)?.name}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[job.status]}`}>{job.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={'/rekrut/jobs/$jobId' as any} params={{ jobId: job.id } as any} className="text-xs text-blue-600 hover:underline">
                        View
                      </Link>
                      {canManage && job.status === 'draft' && (
                        <button type="button" onClick={() => handlePublish(job)} className="text-xs text-green-600 hover:underline">
                          Publish
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-semibold text-gray-800">New Job Posting</h2>
              <button type="button" onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Job Title</label>
                <input required className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600">Division</label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formNewDivision}
                      onChange={(e) => {
                        setFormNewDivision(e.target.checked);
                        if (!e.target.checked) setForm((f) => ({ ...f, department: '' }));
                      }}
                      className="rounded"
                    />
                    Starting a new division
                  </label>
                </div>
                {formNewDivision ? (
                  <input
                    required
                    placeholder="New division name…"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.department}
                    onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                  />
                ) : (
                  <select
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formDivisionId}
                    onChange={(e) => setFormDivisionId(e.target.value)}
                  >
                    <option value="">— Select division —</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Seniority Level</label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formOrgLevelId}
                  onChange={(e) => setFormOrgLevelId(e.target.value)}
                >
                  <option value="">— Any level —</option>
                  {orgLevels.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea rows={3} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              {createError && <p className="text-xs text-red-600">{createError}</p>}
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">{creating ? 'Creating…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
